import React, { useRef, useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";
import { money } from "../lib/api";

/**
 * Horizontal, snap-to product carousel. Shows 3 cards per row on md+ and 1.5
 * on mobile. Each card is a button that fires `onPick(product)`. Arrow
 * controls appear only when there is overflow.
 */
export default function ProductCarousel({ products, onPick, testIdPrefix = "prod", size = "md" }) {
  const scrollerRef = useRef(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  const update = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    setAtStart(el.scrollLeft <= 2);
    setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 2);
  }, []);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    update();
    el.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      el.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [update, products?.length]);

  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const step = el.clientWidth; // scroll by one viewport
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  if (!products || products.length === 0) return null;

  const showArrows = products.length > (size === "sm" ? 4 : 3);
  const cardWidth =
    size === "sm"
      ? "w-[48%] sm:w-[32%] md:w-[calc((100%-2.25rem)/4)]"
      : "w-[65%] sm:w-[45%] md:w-[calc((100%-1.5rem)/3)]";
  const cardPad = size === "sm" ? "p-3" : "p-4";
  const nameSize = size === "sm" ? "text-[13px]" : "text-sm";
  const priceSize = size === "sm" ? "text-lg" : "text-xl";

  return (
    <div className="relative -mx-4 md:mx-0">
      {showArrows && (
        <>
          <button
            type="button"
            aria-label="Anterior"
            onClick={() => scrollBy(-1)}
            disabled={atStart}
            className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 -translate-x-3 z-10 h-10 w-10 rounded-full bg-white border border-neutral-200 shadow-md items-center justify-center hover:bg-neutral-50 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid={`${testIdPrefix}-carousel-prev`}
          >
            <ChevronLeft size={18} />
          </button>
          <button
            type="button"
            aria-label="Siguiente"
            onClick={() => scrollBy(1)}
            disabled={atEnd}
            className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-3 z-10 h-10 w-10 rounded-full bg-white border border-neutral-200 shadow-md items-center justify-center hover:bg-neutral-50 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid={`${testIdPrefix}-carousel-next`}
          >
            <ChevronRight size={18} />
          </button>
        </>
      )}
      <div
        ref={scrollerRef}
        className="flex gap-3 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-2 px-4 md:px-0 no-scrollbar"
        data-testid={`${testIdPrefix}-carousel`}
      >
        {products.map((p) => (
          <button
            type="button"
            key={p.product_id}
            onClick={() => onPick(p)}
            className={`group snap-start shrink-0 ${cardWidth} text-left rounded-2xl border border-neutral-200 ${cardPad} bg-white hover:border-company hover:shadow-md transition-all focus:outline-none focus:ring-2 focus:ring-[color:var(--company-primary)] focus:ring-offset-2`}
            data-testid={`product-card-${p.product_id}`}
          >
            <p className={`font-semibold ${nameSize} leading-tight line-clamp-2 min-h-[2.4em]`}>{p.name}</p>
            <p className="text-[11px] text-neutral-500 mt-1">
              {p.kind === "cylinder"
                ? `${p.size_kg} kg`
                : `por ${p.unit === "liter" ? "litro" : "kilogramo"}`}
            </p>
            <div className="mt-2 flex items-end justify-between gap-2">
              <span className={`font-display font-extrabold ${priceSize}`}>{money(p.price)}</span>
              <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[color:var(--company-primary)] opacity-0 group-hover:opacity-100 transition-opacity">
                Pedir <ArrowRight size={12} />
              </span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
